/**
 * Hotspot folders and the visitor directory: the scene contract shared by the editor
 * (which organises hotspots into folders) and the viewer (whose directory lists them).
 *
 * Scene JSON:
 * - `hotspotFolders: { folders: [{ id, name, parentId? }] }`: one folder tree, any depth. A folder
 *   with `parentId` is inside that folder; without one (or naming a folder that doesn't exist, or
 *   one that would make a loop) it is at the top. Siblings are in array order; the editor saves
 *   the array depth-first (a folder, then what is inside it), so a viewer that predates nesting
 *   lists the folders flat in a sensible order.
 * - A hotspot names its folder with `folderId`, and so may an extra waypoint (one tree for both,
 *   so a "Restaurants" folder can hold the restaurants' hotspots and their views). Without one (or
 *   naming a folder that no longer exists) it is "not in a folder".
 * - The scene's `hotspots` array follows the editor's order: hotspots not in a folder first, then
 *   each folder's (folderItemOrder: inside a folder, its subfolders' hotspots come before its own).
 *   Extra waypoints follow the same order. Folders on their own never change what visitors see.
 * - `visitorDirectory: { enabled, title?, startOpen?, viewOrder? }`: the visitor directory, a panel
 *   that lists the hotspots by folder (visitors go to one, and tick folders on and off) and
 *   "Views": the extra waypoints (one with `hideInDirectory` is left out; one in a folder is listed
 *   in it) and the tour waypoints with `showInDirectory` (directoryViews). Off unless `enabled` is true.
 *
 * Pure functions only, so both sides and their tests can use them.
 */

export interface HotspotFolder {
    id: string;
    name: string;
    /** The folder it is in. Absent: at the top. */
    parentId?: string;
}

export interface HotspotFoldersConfig {
    folders: HotspotFolder[];
}

export interface VisitorDirectoryConfig {
    /** Visitors get a toolbar button that opens the directory (when it lists anything). Default off. */
    enabled?: boolean;
    /** Its title (the button's label and the panel heading). Default "Points of interest". */
    title?: string;
    /** Open the directory when the scene starts. Default off. */
    startOpen?: boolean;
    /**
     * The order of the Views, by id (tour waypoints and extra waypoints). Views it doesn't name
     * follow in their default order: tour waypoints in tour order, then extra waypoints.
     */
    viewOrder?: string[];
}

export const DEFAULT_DIRECTORY_TITLE = 'Points of interest';
/** Longest folder name kept (longer names are cut). */
export const MAX_HOTSPOT_FOLDER_NAME = 80;
/** Longest directory title kept. */
export const MAX_DIRECTORY_TITLE = 60;
/** The directory shows a filter box when it lists more hotspots than this. */
export const DIRECTORY_FILTER_THRESHOLD = 12;
/** Most ids kept in `viewOrder`. */
export const MAX_DIRECTORY_VIEW_ORDER = 512;

/** The fields of a hotspot (or an extra waypoint) the folder helpers read. */
export interface FolderedHotspot {
    id: string;
    folderId?: string | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);

/** A folder name as stored: trimmed, at most MAX_HOTSPOT_FOLDER_NAME characters, never empty. */
export function cleanHotspotFolderName(name: unknown, fallback = 'Folder'): string {
    const text = typeof name === 'string' ? name.replace(/\s+/g, ' ').trim().slice(0, MAX_HOTSPOT_FOLDER_NAME).trim() : '';
    return text || fallback;
}

/**
 * The folders of a saved scene: entries with a string id (the first of a repeated id wins) and a
 * name. A `parentId` is kept when it names another folder and doesn't make a loop: going through
 * the folders in order, one whose parents lead back to itself goes to the top, which frees the
 * rest of the loop. Undefined when there are none, so old scenes and scenes without folders stay
 * unchanged.
 */
export function sanitizeHotspotFolders(raw: unknown): HotspotFoldersConfig | undefined {
    if (!isRecord(raw) || !Array.isArray(raw.folders)) return undefined;
    const seen = new Set<string>();
    const folders: HotspotFolder[] = [];
    const parents = new Map<string, unknown>();
    for (const entry of raw.folders) {
        if (!isRecord(entry) || typeof entry.id !== 'string' || !entry.id.trim() || seen.has(entry.id)) continue;
        seen.add(entry.id);
        folders.push({ id: entry.id, name: cleanHotspotFolderName(entry.name) });
        parents.set(entry.id, entry.parentId);
    }
    if (!folders.length) return undefined;
    const parentOf = new Map<string, string>();
    for (const folder of folders) {
        const parent = parents.get(folder.id);
        if (typeof parent === 'string' && parent !== folder.id && seen.has(parent)) parentOf.set(folder.id, parent);
    }
    for (const folder of folders) {
        // Walk up. Back at itself: it is on a loop, so it goes to the top. Another loop ahead (one
        // it only leads into) is broken at that loop's own first folder.
        const visited = new Set<string>();
        let at = parentOf.get(folder.id);
        while (at !== undefined && at !== folder.id && !visited.has(at)) {
            visited.add(at);
            at = parentOf.get(at);
        }
        if (at === folder.id) parentOf.delete(folder.id);
    }
    return { folders: folders.map((f) => (parentOf.has(f.id) ? { ...f, parentId: parentOf.get(f.id)! } : f)) };
}

/**
 * The folder tree of a sanitized config: each folder's children (top-level folders under `null`),
 * siblings in array order. Every folder is reachable from the top (sanitizeHotspotFolders).
 */
export function folderChildren(config: HotspotFoldersConfig | undefined | null): Map<string | null, HotspotFolder[]> {
    const folders = config?.folders ?? [];
    const ids = new Set(folders.map((f) => f.id));
    const children = new Map<string | null, HotspotFolder[]>();
    for (const folder of folders) {
        const parent = folder.parentId && ids.has(folder.parentId) && folder.parentId !== folder.id ? folder.parentId : null;
        const list = children.get(parent);
        if (list) list.push(folder);
        else children.set(parent, [folder]);
    }
    return children;
}

/**
 * Every folder in tree order (a folder, then what is inside it, depth-first; siblings in array
 * order) with its depth (0 at the top). Folders a malformed config leaves unreachable (a loop the
 * config wasn't sanitized for) come last at depth 0, so none is ever lost.
 */
export function folderTreeOrder(config: HotspotFoldersConfig | undefined | null): Array<{ folder: HotspotFolder; depth: number }> {
    const children = folderChildren(config);
    const out: Array<{ folder: HotspotFolder; depth: number }> = [];
    const placed = new Set<string>();
    // Iterative, so a very deep tree can't overflow the stack.
    const stack: Array<{ folder: HotspotFolder; depth: number }> = [...(children.get(null) ?? [])].reverse().map((folder) => ({ folder, depth: 0 }));
    while (stack.length) {
        const entry = stack.pop()!;
        if (placed.has(entry.folder.id)) continue;
        placed.add(entry.folder.id);
        out.push(entry);
        const kids = children.get(entry.folder.id) ?? [];
        for (let i = kids.length - 1; i >= 0; i--) stack.push({ folder: kids[i], depth: entry.depth + 1 });
    }
    for (const folder of config?.folders ?? []) if (!placed.has(folder.id)) out.push({ folder, depth: 0 });
    return out;
}

/** A folder and every folder inside it, at any depth (empty for an unknown id). */
export function folderSubtreeIds(config: HotspotFoldersConfig | undefined | null, id: string): Set<string> {
    const children = folderChildren(config);
    const out = new Set<string>();
    if (!(config?.folders ?? []).some((f) => f.id === id)) return out;
    const stack = [id];
    while (stack.length) {
        const next = stack.pop()!;
        if (out.has(next)) continue;
        out.add(next);
        for (const child of children.get(next) ?? []) stack.push(child.id);
    }
    return out;
}

/** The folders from the top down to `id` (itself last); empty for an unknown id. */
export function folderPath(config: HotspotFoldersConfig | undefined | null, id: string | null | undefined): HotspotFolder[] {
    const byId = new Map((config?.folders ?? []).map((f) => [f.id, f]));
    const out: HotspotFolder[] = [];
    let at = id ? byId.get(id) : undefined;
    while (at && !out.includes(at)) {
        out.unshift(at);
        at = at.parentId ? byId.get(at.parentId) : undefined;
    }
    return out;
}

/**
 * The folders in the order their contents come in the scene's lists: inside a folder its
 * subfolders' contents come before its own (the editor and the directory show subfolders first).
 */
export function folderItemOrder(config: HotspotFoldersConfig | undefined | null): HotspotFolder[] {
    const order = folderTreeOrder(config);
    const out: HotspotFolder[] = [];
    // Post-order from the pre-order: a folder goes out once everything deeper after it has.
    const open: Array<{ folder: HotspotFolder; depth: number }> = [];
    for (const entry of order) {
        while (open.length && open[open.length - 1].depth >= entry.depth) out.push(open.pop()!.folder);
        open.push(entry);
    }
    while (open.length) out.push(open.pop()!.folder);
    return out;
}

/** The directory settings of a saved scene; undefined when they are absent or not an object. */
export function sanitizeVisitorDirectory(raw: unknown): VisitorDirectoryConfig | undefined {
    if (!isRecord(raw)) return undefined;
    const out: VisitorDirectoryConfig = {};
    if (raw.enabled === true) out.enabled = true;
    if (typeof raw.title === 'string' && raw.title.trim()) out.title = raw.title.trim().slice(0, MAX_DIRECTORY_TITLE);
    if (raw.startOpen === true) out.startOpen = true;
    if (Array.isArray(raw.viewOrder)) {
        const ids = [...new Set(raw.viewOrder.filter((id): id is string => typeof id === 'string' && !!id))].slice(0, MAX_DIRECTORY_VIEW_ORDER);
        if (ids.length) out.viewOrder = ids;
    }
    return Object.keys(out).length ? out : undefined;
}

/** The directory's title as visitors see it. */
export function visitorDirectoryTitle(config: VisitorDirectoryConfig | undefined | null): string {
    return config?.title?.trim() || DEFAULT_DIRECTORY_TITLE;
}

/** The folder a hotspot is in, or null when it isn't in one (or names a folder that doesn't exist). */
export function hotspotFolderIdOf(hotspot: FolderedHotspot, folderIds: ReadonlySet<string>): string | null {
    const id = hotspot.folderId;
    return typeof id === 'string' && folderIds.has(id) ? id : null;
}

export interface HotspotFolderGroup<T> {
    folder: HotspotFolder;
    hotspots: T[];
}

export interface HotspotFolderGroups<T> {
    /** Hotspots not in a folder, in scene order. */
    unfiled: T[];
    /** Every folder in tree order (folderTreeOrder) with its hotspots in scene order (empty folders included). */
    folders: HotspotFolderGroup<T>[];
}

/** Hotspots grouped by folder, each group keeping the scene's relative order. Folders in tree order (folderTreeOrder). */
export function groupHotspotsByFolder<T extends FolderedHotspot>(
    hotspots: readonly T[],
    config: HotspotFoldersConfig | undefined | null,
): HotspotFolderGroups<T> {
    const groups = new Map<string, HotspotFolderGroup<T>>(folderTreeOrder(config).map(({ folder }) => [folder.id, { folder, hotspots: [] }]));
    const ids = new Set(groups.keys());
    const unfiled: T[] = [];
    for (const hotspot of hotspots) {
        const folderId = hotspotFolderIdOf(hotspot, ids);
        if (folderId === null) unfiled.push(hotspot);
        else groups.get(folderId)!.hotspots.push(hotspot);
    }
    return { unfiled, folders: [...groups.values()] };
}

/**
 * The scene order of the hotspots (or extra waypoints): not in a folder first, then each folder's,
 * in folderItemOrder. Returns the same array when it already is in that order (always without folders).
 */
export function orderHotspotsByFolder<T extends FolderedHotspot>(hotspots: readonly T[], config: HotspotFoldersConfig | undefined | null): T[] {
    if (!config?.folders.length) return hotspots as T[];
    const groups = groupHotspotsByFolder(hotspots, config);
    const byId = new Map(groups.folders.map((g) => [g.folder.id, g.hotspots]));
    const ordered = [...groups.unfiled, ...folderItemOrder(config).flatMap((f) => byId.get(f.id) ?? [])];
    return ordered.every((h, i) => h === hotspots[i]) ? (hotspots as T[]) : ordered;
}

/** The fields of a tour waypoint the directory reads. */
export interface DirectoryTourWaypointSource {
    /** Stable id (the editor sets one when the waypoint is put in the directory). */
    id?: string;
    name?: string;
    /** List it under Views. Default: not listed (the waypoints menu lists tour waypoints). */
    showInDirectory?: boolean;
}

/** The fields of an extra waypoint the directory reads. */
export interface DirectoryExtraWaypointSource {
    id: string;
    name?: string;
    /** Leave it out of Views. Default: listed. */
    hideInDirectory?: boolean;
    /** The folder it is listed in (hotspotFolders). Absent: under Views. */
    folderId?: string | null;
}

/** One entry of the directory's Views. */
export interface DirectoryViewEntry {
    /** Its id in `viewOrder`: the waypoint's id; a tour waypoint without one (or with a repeated one) gets `waypoint-<n>`. */
    id: string;
    /** A tour waypoint (goes there on the tour) or an extra waypoint (flies there in explore mode). */
    kind: 'waypoint' | 'extra';
    /** Its index in the tour (kind 'waypoint') or in the extra waypoints list. */
    index: number;
    /** Its name, or "Waypoint n" (tour index) / "View n" (place among the listed extra waypoints). */
    name: string;
}

/**
 * The directory's Views in the order visitors see them: the tour waypoints with `showInDirectory`
 * and the extra waypoints without `hideInDirectory`, sorted by `viewOrder`. Views it doesn't name
 * follow in their default order (tour waypoints in tour order, then extra waypoints). The first of
 * a repeated id wins; a later extra waypoint with it is left out.
 */
export function directoryViews(
    waypoints: readonly (DirectoryTourWaypointSource | null | undefined)[] | null | undefined,
    extraWaypoints: readonly (DirectoryExtraWaypointSource | null | undefined)[] | null | undefined,
    viewOrder?: readonly string[] | null,
): DirectoryViewEntry[] {
    const seen = new Set<string>();
    const views: DirectoryViewEntry[] = [];
    const named = (name: string | undefined, fallback: string) => (typeof name === 'string' && name.trim() ? name.trim() : fallback);
    (Array.isArray(waypoints) ? waypoints : []).forEach((wp, index) => {
        if (!wp || wp.showInDirectory !== true) return;
        let id = typeof wp.id === 'string' && wp.id ? wp.id : `waypoint-${index + 1}`;
        if (seen.has(id)) id = `waypoint-${index + 1}`;
        if (seen.has(id)) return;
        seen.add(id);
        views.push({ id, kind: 'waypoint', index, name: named(wp.name, `Waypoint ${index + 1}`) });
    });
    let listedExtras = 0;
    (Array.isArray(extraWaypoints) ? extraWaypoints : []).forEach((wp, index) => {
        if (!wp || typeof wp.id !== 'string' || !wp.id || wp.hideInDirectory === true || seen.has(wp.id)) return;
        seen.add(wp.id);
        listedExtras += 1;
        views.push({ id: wp.id, kind: 'extra', index, name: named(wp.name, `View ${listedExtras}`) });
    });
    if (!viewOrder?.length) return views;
    const rank = new Map(viewOrder.map((id, i) => [id, i]));
    return views
        .map((view, i) => ({ view, key: rank.get(view.id) ?? viewOrder.length + i }))
        .sort((a, b) => a.key - b.key)
        .map((entry) => entry.view);
}

/**
 * `viewOrder` after a View moves from position `from` to `to` in `views` (directoryViews): the ids
 * of the listed views in their new order. A view listed later goes after them.
 */
export function moveDirectoryView(views: readonly DirectoryViewEntry[], from: number, to: number): string[] {
    const ids = views.map((v) => v.id);
    if (from < 0 || from >= ids.length || to < 0 || to >= ids.length) return ids;
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved);
    return ids.slice(0, MAX_DIRECTORY_VIEW_ORDER);
}
